<template>
  <div class="table-container" ref="c3tcontainer"></div>
</template>

<script>
import repository from '@/repositories/Repository'

/**
 * C3-Tableのラッパーコンポーネント
 *
 * C3Tableインスタンスを内包し、渡されたデータを描画します。
 */
export default {
  props: {
    /**
     * データ取得URL
     */
    url: {
      type: String,
    },
    /**
     * 行選択の有効無効
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * 出力する行データ
     *
     * URLが指定されている場合、そちらが優先されます。
     */
    rows: {
      type: Array,
      default: () => [],
    },
    /**
     * 列モデル
     */
    columns: {
      type: Array,
      default: () => [],
    },
    groupHeaders: {
      type: Array,
    },
    sortColumnName: {
      type: String,
    },
    sortOrder: {
      type: String,
      required: false,
      validator(v) {
        return ['asc', 'desc'].includes(v)
      },
    },
    numberOfRows: {
      type: Number,
      default: 100,
    },
    /**
     * グリッドの横幅
     */
    width: {
      type: Number,
      default: 0,
    },
    /**
     * グリッドの高さ
     */
    height: {
      type: Number,
      default: 0,
    },
    /**
     * Column.cellとバインドするためのカスタムフォーマッター
     */
    customFormatter: {
      type: Object,
    },
  },
  data: () => {
    return {
      c3table: undefined,
    }
  },
  methods: {
    init() {
      if (!this.columns || this.columns.length === 0) {
        return
      }

      // this.columnsはVue管理のObserverであり、C3Tableにそのまま渡すとうまく
      // 動かないので変換する。
      // TODO もうちょっとエレガントな方法はないか？「{...this.columns}」ではダメだった。。。
      const columns = this.columns.map(c => ({
        colName: c.colName,
        headerText: c.headerText,
        width: c.width,
        align: c.align,
        fixed: c.fixed,
        defaultSort: c.defaultSort,
        sortable: c.sortable,
        hidden: c.hidden,
        headerCell: c.headerCell,
        headerContent: c.headerContent,
        rowspan: c.rowspan,
        rowspanStep: c.rowspanStep,
        cell: c.cell,
        customFormatter: c.customFormatter,
        format: c.format,
        type: c.type,
        suffix: c.suffix,
        rowspanUniqueColName: c.rowspanUniqueColName,
        editable: c.editable,
        outputExcel: c.outputExcel,
        outputCSV: c.outputCSV,
      }))

      if (this.c3table) {
        this.c3table.clearData()
      }

      // eslint-disable-next-line no-undef
      this.c3table = new C3Table({
        container: this.$refs.c3tcontainer,
        columns,
        groupHeaders: this.groupHeaders,
        numberOfRows: this.numberOfRows,
        sortColumnName: this.sortColumnName,
        sortOrder: this.sortOrder,
        selectable: this.selectable,
        width: this.width,
        height: this.height,
        url: this.url,
        customFormatter: this.customFormatter,
        onFetch: (url, params) => {
          if (url.indexOf('?') === -1) {
            url += '?'
          }
          if (params) {
            for (const k of Object.keys(params)) {
              url += `&${k}=${params[k]}`
            }
          }

          return repository.giggle.get(url).then(r => r.data)
        },
      })

      this.c3table.build()

      if (this.url) {
        this.c3table.load()
      }

      this.$emit('initialized', {
        headerTable: this.c3table.headerTable,
      })
    },
    /**
     * 現在ソートされている列の列名と順を取得します
     */
    getCurrentSort() {
      if (!this.c3table) {
        throw new Error('テーブルが初期化されていません')
      }

      return { ...this.c3table.currentSort }
    },
    /**
     * 選択されている行のデータを取得する
     */
    getSelectedRowData() {
      if (!this.c3table) {
        throw new Error('テーブルが初期化されていません')
      }

      const map = this.c3table.elemDataMap
      return Array.from(this.c3table.selectedRows).map(r => map.get(r))
    },
    /**
     * ヘッダの全選択のチェックボックスのInput要素を取得
     */
    isSelectAllChecked() {
      const allCheckInput = document.querySelector(
        'th[col-name="cb"].c3t-header-cell.c3t-cell.c3t-col-type-input.c3t-center input',
      )
      return allCheckInput ? allCheckInput.checked : false
    },
  },
  watch: {
    width(w) {
      if (this.c3table) {
        this.c3table.setWidth(w)
      }
    },
    height(h) {
      if (this.c3table) {
        this.c3table.setHeight(h)
      }
    },
    columns: {
      handler: 'init',
    },
    url: {
      handler: 'init',
    },
    rows(rows) {
      if (!this.c3table) {
        // not initialized
        return
      }

      if (this.url) {
        // データ取得URLが指定されている場合、そもそもrowsを更新してくる
        // ことはないと思うが、一応対処。
        return
      }

      // 描画する全行を渡してきているはずなので、洗い替え
      this.c3table.clearData()
      this.c3table.load(rows)
    },
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    if (this.c3table) {
      this.c3table.clearData()
    }
  },
}
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
